import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class WorkspaceSwitcherService {
    
    // User's available roles based on group membership
    private availableRolesSubject = new BehaviorSubject<string[]>([]);
    public availableRoles$ = this.availableRolesSubject.asObservable();

    // Current active workspace/role
    private currentWorkspaceSubject = new BehaviorSubject<string>('');
    public currentWorkspace$ = this.currentWorkspaceSubject.asObservable();

    constructor() {
        console.log('[WorkspaceSwitcherService] Initialized');
    }

    /**
     * Set user's available roles and initialize workspace
     * @param roles Array of role names user is member of
     */
    public setAvailableRoles(roles: string[]): void {
        console.log('[WorkspaceSwitcherService] setAvailableRoles - Setting available roles:', roles);
        this.availableRolesSubject.next(roles);
        
        // Default to admin if available, otherwise first available role
        if (roles.length > 0) {
            const defaultRole = roles.includes('admin') ? 'admin' : roles[0];
            this.setCurrentWorkspace(defaultRole);
            console.log('[WorkspaceSwitcherService] setAvailableRoles - Default workspace set to:', defaultRole);
        }
    }

    /**
     * Get current available roles
     */
    public getAvailableRoles(): string[] {
        return this.availableRolesSubject.value;
    }

    /**
     * Switch to a different workspace/role
     * @param role Role name to switch to
     * @returns boolean - true if switch successful, false if role not available
     */
    public switchWorkspace(role: string): boolean {
        const availableRoles = this.availableRolesSubject.value;
        
        if (!availableRoles.includes(role)) {
            console.warn('[WorkspaceSwitcherService] switchWorkspace - Role not available:', role);
            return false;
        }

        console.log('[WorkspaceSwitcherService] switchWorkspace - Switching to role:', role);
        this.setCurrentWorkspace(role);
        return true;
    }

    /**
     * Set current workspace (private helper)
     */
    private setCurrentWorkspace(role: string): void {
        console.log('[WorkspaceSwitcherService] setCurrentWorkspace - Current workspace changed to:', role);
        this.currentWorkspaceSubject.next(role);
        
        // Persist to sessionStorage
        sessionStorage.setItem('currentWorkspace', role);
    }

    /**
     * Get current workspace
     */
    public getCurrentWorkspace(): string {
        return this.currentWorkspaceSubject.value;
    }

    /**
     * Restore workspace from sessionStorage if exists
     */
    public restoreWorkspace(): void {
        const saved = sessionStorage.getItem('currentWorkspace');
        if (saved && this.availableRolesSubject.value.includes(saved)) {
            console.log('[WorkspaceSwitcherService] restoreWorkspace - Restoring workspace:', saved);
            this.currentWorkspaceSubject.next(saved);
        }
    }

    /**
     * Clear workspace data (on logout)
     */
    public clear(): void {
        console.log('[WorkspaceSwitcherService] clear - Clearing workspace data');
        this.availableRolesSubject.next([]);
        this.currentWorkspaceSubject.next('');
        sessionStorage.removeItem('currentWorkspace');
    }

    /**
     * Check if user has a specific role
     */
    public hasRole(role: string): boolean {
        return this.availableRolesSubject.value.includes(role);
    }

    /**
     * Check if user is in a specific workspace
     */
    public isInWorkspace(role: string): boolean {
        return this.currentWorkspaceSubject.value === role;
    }

    /**
     * Check if current workspace is admin
     * Returns true if user is currently in the admin workspace
     */
    public isAdminOnlyWorkspace(): boolean {
        const currentWorkspace = this.currentWorkspaceSubject.value;
        return currentWorkspace === 'admin';
    }

    /**
     * Check if workspace switching is enabled (user has multiple roles)
     */
    public isSwitchingEnabled(): boolean {
        return this.availableRolesSubject.value.length > 1;
    }
}
